import React, { useMemo } from 'react';
import useTranslate from '../../../../../customHooks/useTranslate';
import {
    ConnectionRequestState,
    ZinantisRelationshipStatusTypeKeys,
} from '../../../../../types/zinantis/members/membersType';
import { useZinantisMembers } from '../../../../contextsProperty/ContextZinantisMembers';
import Title from '../../../../components/Title';
import { ConnetionRequestSource } from '../../../../../types/zinantis/connectionRequests/connectionRequestTypes';
import Icon from '../../../../../muiComponents/Icon';
import { formatDate, formatDateTime } from '../../../../../utils/formatDates';
import useLanguage from '../../../../../customHooks/useLanguage';
import { Link } from 'react-router-dom';
import routes from '../../../../../utils/commonVariables/routes';
import CancelRequestButton from '../../requestButtons/CancelRequestButton';
import RejectConnectionButton from '../../requestButtons/RejectConexionButton';
import RequestConnectionButton from '../../requestButtons/RequestConnectionButton';
import Tooltip from '../../../../../muiComponents/Tooltip';
import useTimeZone from '../../../../../customHooks/useTimeZone';
import AcceptConnectionButton from '../../requestButtons/AcceptConnectionButton';
import Status from '../../../../../components/Status';
import { PENDING_REQUEST_STATUS_KEY } from '../../../../../utils/zinantis/requests/requestsUtils';
import { IZinantisConnectionRequest } from '../../../../../types/entities/IZinantisConnectionRequest';
import { capitalize } from '../../../../../utils/usefulFunctions';

interface ConnectionRequestSectionProps {
    idAgency: string;
    status: string;
    statusColor: string;
    statusKey: ZinantisRelationshipStatusTypeKeys;
    connectionRequests: ConnectionRequestState[];
    onChangeLoading: (value: boolean) => void;
    ownerName: string;
    agencyName: string;
}

const ConnectionRequestSection = (props: ConnectionRequestSectionProps) => {
    const {
        idAgency,
        statusKey,
        status,
        statusColor,
        connectionRequests,
        onChangeLoading,
        ownerName,
        agencyName,
    } = props;
    const translate = useTranslate();
    const language = useLanguage();
    const timeZone = useTimeZone();
    const { addConnectedAgency, disconnectAgency, updateRequest } =
        useZinantisMembers();

    const incomingRequest = useMemo(() => {
        return connectionRequests.find(
            request => request.requestedBy === ConnetionRequestSource.AGENCY
        );
    }, [connectionRequests]);

    const outgoingRequest = useMemo(() => {
        return connectionRequests.find(
            request => request.requestedBy === ConnetionRequestSource.OWNER
        );
    }, [connectionRequests]);

    const isVisibleRequestConnectionButton = useMemo(() => {
        return statusKey === ZinantisRelationshipStatusTypeKeys.DISCONNECTED;
    }, [statusKey]);

    const isVisibleAcceptButton = useMemo(() => {
        return (
            statusKey === ZinantisRelationshipStatusTypeKeys.PENDING &&
            incomingRequest &&
            incomingRequest.statusKey === PENDING_REQUEST_STATUS_KEY
        );
    }, [statusKey, incomingRequest]);

    const isVisibleRejectButton = useMemo(() => {
        return (
            statusKey === ZinantisRelationshipStatusTypeKeys.PENDING &&
            incomingRequest &&
            incomingRequest.statusKey === PENDING_REQUEST_STATUS_KEY
        );
    }, [statusKey, incomingRequest]);

    const isVisibleCancelRequestButton = useMemo(() => {
        return (
            statusKey === ZinantisRelationshipStatusTypeKeys.PENDING &&
            outgoingRequest &&
            !incomingRequest
        );
    }, [statusKey, outgoingRequest]);

    const requestedByText = useMemo(() => {
        if (!connectionRequests.length) return '';
        switch (connectionRequests.length) {
            case 1:
                switch (connectionRequests[0].requestedBy) {
                    case 0:
                        return agencyName || 'Agency';
                    case 1:
                        return ownerName || 'Hotel Owner';
                    default:
                        return '';
                }
            case 2:
                return 'Zinantis';
            default:
                return '';
        }
    }, [connectionRequests, agencyName, ownerName]);

    const requestDate =
        incomingRequest?.requestDate || outgoingRequest?.requestDate || '';

    const resolutionDate =
        incomingRequest?.resolutionDate ||
        outgoingRequest?.resolutionDate ||
        '';

    const handleRequestSuccess = async (
        newRequest: IZinantisConnectionRequest
    ) => {
        addConnectedAgency(idAgency, {
            id: newRequest.Id,
            requestDate: newRequest.RequestDate,
            requestedBy:
                newRequest.RequestedBy === ConnetionRequestSource.AGENCY
                    ? ConnetionRequestSource.AGENCY
                    : ConnetionRequestSource.OWNER,
            resolutionDate: newRequest.ResolutionDate,
            statusKey: PENDING_REQUEST_STATUS_KEY,
        });
    };

    const handleCancelRequestSuccess = async () => {
        disconnectAgency(idAgency);
    };

    const handleAcceptRequestSuccess = async (
        newRequest: IZinantisConnectionRequest
    ) => {
        const updatedRequest = {
            id: newRequest.Id,
            requestDate: newRequest.RequestDate,
            requestedBy: newRequest.RequestedBy,
            resolutionDate: newRequest.ResolutionDate,
            statusKey: 'ACCEPTED',
        };
        updateRequest(idAgency, updatedRequest);
    };

    const handleRejectRequestSuccess = async () => {
        disconnectAgency(idAgency);
    };

    return (
        <div className='member-panel__connection-request-section flex-grow flex-column gap-20'>
            <table>
                <tr className='height-30'>
                    <td>
                        <Title>{translate('status')}</Title>
                    </td>
                    <td>
                        <Status text={capitalize(status)} color={statusColor} />
                    </td>
                </tr>
                {requestedByText ? (
                    <tr className='height-30'>
                        <td>
                            <Title style={{ overflow: 'unset' }}>
                                {translate('requested_by')}
                            </Title>
                        </td>
                        <td>
                            <p className='font-weight-600 '>
                                {requestedByText}
                            </p>
                        </td>
                    </tr>
                ) : (
                    ''
                )}
                {statusKey === ZinantisRelationshipStatusTypeKeys.PENDING &&
                requestDate ? (
                    <tr className='height-30'>
                        <td>
                            <Title>{translate('request_date')}</Title>
                        </td>
                        <td>
                            <Tooltip
                                title={formatDateTime(
                                    requestDate,
                                    language,
                                    timeZone
                                )}
                            >
                                <p className='width-content'>
                                    {requestDate
                                        ? formatDate(requestDate, language)
                                        : ''}
                                </p>
                            </Tooltip>
                        </td>
                    </tr>
                ) : (
                    ''
                )}
                {statusKey === ZinantisRelationshipStatusTypeKeys.CONFIRMED &&
                resolutionDate ? (
                    <tr className='height-30'>
                        <td>
                            <Title>{translate('resolution_date')}</Title>
                        </td>
                        <td>
                            <Tooltip
                                title={formatDateTime(
                                    resolutionDate,
                                    language,
                                    timeZone
                                )}
                            >
                                <p className='width-content'>
                                    {formatDate(resolutionDate, language)}
                                </p>
                            </Tooltip>
                        </td>
                    </tr>
                ) : (
                    ''
                )}
                <tr>
                    <td
                        style={{ verticalAlign: 'bottom' }}
                        className='width-50-perc'
                    >
                        <Link
                            className='width-content gap-10 flex flex-baseline'
                            to={
                                routes.landingPrivate +
                                routes.zinantisSection +
                                routes.zinantisConnectionRequests
                            }
                        >
                            <Icon iconName='Cable' />
                            {translate('manage_all_requests')}
                        </Link>
                    </td>
                    <td>
                        <div className='flex gap-20 height-50 flex-align-end'>
                            {isVisibleRequestConnectionButton ? (
                                <RequestConnectionButton
                                    onChangeLoading={onChangeLoading}
                                    onRequestSuccess={handleRequestSuccess}
                                    idAgency={idAgency}
                                />
                            ) : (
                                ''
                            )}
                            {isVisibleCancelRequestButton && outgoingRequest ? (
                                <CancelRequestButton
                                    onChangeLoading={onChangeLoading}
                                    onRequestSuccess={
                                        handleCancelRequestSuccess
                                    }
                                    idRequest={outgoingRequest.id}
                                />
                            ) : (
                                ''
                            )}

                            {isVisibleRejectButton && incomingRequest ? (
                                <RejectConnectionButton
                                    onChangeLoading={onChangeLoading}
                                    onRequestSuccess={
                                        handleRejectRequestSuccess
                                    }
                                    idRequest={incomingRequest.id}
                                />
                            ) : (
                                ''
                            )}
                            {isVisibleAcceptButton && incomingRequest ? (
                                <AcceptConnectionButton
                                    onChangeLoading={onChangeLoading}
                                    onRequestSuccess={
                                        handleAcceptRequestSuccess
                                    }
                                    idRequest={incomingRequest.id}
                                />
                            ) : (
                                ''
                            )}
                        </div>
                    </td>
                </tr>
            </table>
        </div>
    );
};

export default ConnectionRequestSection;
